import React, { useEffect, useState, useMemo } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import { createSelector } from "reselect";

import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";

import InsuranceContainer from "./InsuranceContainer";
import { RootState, AppDispatch } from "store";
import { changeProductStatusThunk, fetchProductsThunk } from "slices/Insurance/thunk";
import ProductForm from "./ProductForm";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getAllProductBenefits, getAllProductEligibility, getAllProductPremium, getProductAdditionalInfo } from "services/insuranceService";

// 🔹 REDUX SELECTOR
const selectInsuranceState = (state: RootState) => (state as any).insurance;

const insuranceSelector = createSelector(selectInsuranceState, (state) => ({
  products: state.productList?.products || [],
  pagination: state.productList?.pagination || {},
  loading: state.loading,
}));

const InsuranceRecords: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { products, pagination, loading } = useSelector(insuranceSelector);
  const [isNewProduct, setIsNewProduct] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Basic Info"); 

  const toggleModal = () => {
    setIsNewProduct(true); 
    validation.resetForm();
    setActiveTab("Basic Info");
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) {
    dispatch(
      fetchProductsThunk({
        page,
        per_page: limit,
        product_code: "",
        product_category: "",
        product_name: "",
        searchkey: ""
      })
    );
  }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

 const totalProducts =
  pagination?.total_items || products?.length || 0;

const activeProducts =
  pagination?.total_active ?? 0;

const inactiveProducts =
  pagination?.total_inactive ?? 0;

  const handleEditClick = async (data: any) => {
  setIsNewProduct(false);

  // 1. Set Basic Fields
  validation.setFieldValue("id", data.id);
  validation.setFieldValue("product_id", data.id); // keep both for downstream calls
  validation.setFieldValue("product_category", data.product_category);
  validation.setFieldValue("product_name", data.product_name);
  validation.setFieldValue("product_code", data.product_code);
  validation.setFieldValue("plan_type", data.plan_type);
  validation.setFieldValue("product_description", data.product_description);
  validation.setFieldValue("product_image_url", data.product_image_url);
  validation.setFieldValue("status", data.status);

  // 2. CALL APIs
  try {
    const [benefitRes, addInfoRes, eligibilityRes, premiumRes] = await Promise.all([
      getAllProductBenefits(data.id),
      getProductAdditionalInfo(data.id),
      getAllProductEligibility(data.id),
      getAllProductPremium(data.id),
    ]);

    // 3. Set Benefits
    if (benefitRes.data?.length > 0) {
      validation.setFieldValue("benefits", benefitRes.data);
    }

    // 4. Set Additional Info
    const addInfo = Array.isArray(addInfoRes.data)
      ? addInfoRes.data[0]
      : addInfoRes.data;

    if (addInfo) {
      validation.setFieldValue("features_id", addInfo.id);
      Object.keys(addInfo).forEach((key) => {
        validation.setFieldValue(key, addInfo[key]);
      });
      // API sometimes returns `surrender_value`; keep form field (`surrendar_value`) in sync
      if (addInfo.surrender_value && !addInfo.surrendar_value) {
        validation.setFieldValue("surrendar_value", addInfo.surrender_value);
      }
    }

    // 5. Set Eligibility
    const eligibility =
      Array.isArray(eligibilityRes.data) && eligibilityRes.data.length > 0
        ? eligibilityRes.data[0]
        : eligibilityRes.data;

    if (eligibility) {
      validation.setFieldValue("eligibility_id", eligibility.id);
      validation.setFieldValue("min_age", eligibility.min_age);
      validation.setFieldValue("max_age", eligibility.max_age);
      validation.setFieldValue("max_maturity_age", eligibility.max_maturity_age);
      validation.setFieldValue("customer_eligibility", eligibility.customer_eligibility);
      validation.setFieldValue("medical_requirement", eligibility.medical_requirement);
    }

    if (Array.isArray(premiumRes.data) && premiumRes.data.length > 0) {
      const table = premiumRes.data.map((item: any) => ({
        ...item,
        age: item?.age?.toString?.() || item?.age,
        saved: true,
        isEditing: false,
      }));
      validation.setFieldValue("premium_table", table);
      validation.setFieldValue("premium_note", table[0]?.premium_note || "");
    } 
    else if (premiumRes.data && typeof premiumRes.data === "object") {
      // API returned ONLY ONE OBJECT
      validation.setFieldValue("premium_table", [{
        ...premiumRes.data,
        age: premiumRes.data?.age?.toString?.() || premiumRes.data?.age,
        saved: true,
        isEditing: false,
      }]);
      validation.setFieldValue("premium_note", premiumRes.data.premium_note || "");
    } 
    else {
      validation.setFieldValue("premium_table", []);
    }



  } catch (error) {
    console.error(error);
    toast.error("Failed to load product details!");
  }

  // 7. Open Modal
  setActiveTab("Basic Info");
  setIsModalOpen(true);
};


  const validation = useFormik({
    initialValues: {
      id: "",
      product_id: "",
      product_category: "",
      product_name: "",
      product_code: "",
      plan_type: "",
      product_description: "",
      product_image_url: "",
      status: "Active",

      // Key Benefits
      benefits: [],

      // Features
      features_id: "",
      policy_term: "",
      premium_term: "",
      minimum_sum_assured: "",
      maximum_sum_assured: "",
      maturity_bonus_benefit: "",
      death_benefit: "",
      loan_facility: "",
      surrendar_value: "",

      // Eligibility
      eligibility_id: "",
      min_age: "",
      max_age: "",
      max_maturity_age: "",
      customer_eligibility: "",
      medical_requirement: "",

      // Premium
      premium_note: "",
      premium_table: [],
      age: "",
      monthly_bd: "",
      quarterly_bd: "",
      half_yearly_bd: "",
      yearly_bd: "",
      sum_assured: ""
    },

    validationSchema: Yup.object({
      product_name: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      product_category: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      product_code: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      plan_type: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      product_description: Yup.string()
        .required("Required"),
        
      product_image_url: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      benfit_description: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      benfit_title: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      policy_term: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      premium_term: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      minimum_sum_assured: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[0-9]+(\.[0-9]+)?$/, "Only numbers allowed")
        .test(
          "min-less-than-max",
          "Minimum sum assured must be less than maximum sum assured",
          function (value) {
            const { maximum_sum_assured } = this.parent;
            if (!value || !maximum_sum_assured) return true;  
            const minValue = parseFloat(value.toString().trim());
            const maxValue = parseFloat(maximum_sum_assured.toString().trim());
            
            if (isNaN(minValue) || isNaN(maxValue) || !isFinite(minValue) || !isFinite(maxValue)) {
              return true;
            }
            return minValue < maxValue;
          }
        ),

      maximum_sum_assured: Yup.string()
       .required("Required")
       .trim()
       .matches(/^[0-9]+(\.[0-9]+)?$/, "Only numbers allowed")
       .test(
         "max-greater-than-min",
         "Maximum sum assured must be greater than minimum sum assured",
         function (value) {
           const { minimum_sum_assured } = this.parent;
           if (!value || !minimum_sum_assured) return true;
           
           const minValue = parseFloat(minimum_sum_assured.toString().trim());
           const maxValue = parseFloat(value.toString().trim());
           if (isNaN(minValue) || isNaN(maxValue) || !isFinite(minValue) || !isFinite(maxValue)) {
             return true;
           }           
           return maxValue > minValue;
         }
       ),
      maturity_bonus_benefit: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z].*$/, "Only Alphabets allowed")
        .required("Required"),

      death_benefit: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z].*$/, "Only Alphabets allowed")
        .required("Required"),

      loan_facility: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z].*$/, "Only Alphabets allowed")
        .required("Required"),
     
      surrendar_value: Yup.string()
        .matches(/^[A-Za-z].*$/, "Only Alphabets allowed")
        .required("Required"),

      min_age: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      max_age: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      max_maturity_age: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      customer_eligibility: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),
      // surrendar_value:Yup.string()
      //   .required("Required")
      //   .trim()
      //   .matches(/^[A-Za-z0-9].*$/, "Required"),
      medical_requirement: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      premium_note: Yup.string()
        .required("Required"),
        
      age: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      monthly_bd: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      quarterly_bd: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      half_yearly_bd: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      yearly_bd: Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),

      sum_assured:Yup.string()
        .required("Required")
        .trim()
        .matches(/^[A-Za-z0-9].*$/, "Required"),
    }),

    onSubmit: (values) => {
      console.log("FORM SUBMIT", values);
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        fetchProductsThunk({
          page,
          per_page: limit,
          product_code: "",
          product_category: "",
          product_name: "",
          searchkey: searchQuery
        })
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [page, limit, searchQuery, dispatch]);

    const filteredData = useMemo(() => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return products; 

      return products.filter((p: any) =>
        p.product_name?.toLowerCase().includes(q) ||
        p.product_code?.toLowerCase().includes(q) ||
        p.plan_type?.toLowerCase().includes(q) ||
        p.product_additional_info?.policy_term?.toString().toLowerCase().includes(q) 
      );
    }, [products, searchQuery]);


const handleStatusToggle = async (id: number, newStatus: string) => {
  await dispatch(changeProductStatusThunk(id, newStatus));

  dispatch(
    fetchProductsThunk({
      page,
      per_page: limit,
      product_code: "",
      product_category: "",
      product_name: "",
      searchkey: ""
    })
  );
};

  const columns = useMemo(
    () => [
      { header: "Product Code", accessorKey: "product_code" },
      { header: "Name", accessorKey: "product_name" },
      { header: "Plan Type", accessorKey: "plan_type" },
      {
        header: "Policy Term",
        accessorKey: "product_additional_info.policy_term",
        cell: (cell: any) =>
          cell.row.original.product_additional_info?.policy_term || "N/A",
      },
      {
        header: "Sum Assured Range",
        accessorKey: "product_additional_info",
        cell: (cell: any) => {
          const info = cell.row.original.product_additional_info;
          return (
            <div style={{ whiteSpace: "pre-line" }}>
              Min: {info?.minimum_sum_assured ?? "0"}
              {"\n"}
              Max: {info?.maximum_sum_assured ?? "0"}
            </div>
          );
        },
      },
     {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const value = cell.getValue();
        const lowerVal = value.toString().toLowerCase();

        // 🔹 Define color based on status
        const statusColor =
          lowerVal === "active" ? "#159E3A" : "#A65F00"; // Green for Active, Red for Inactive

        return (
          <select
            className="form-select form-select-sm"
            value={cell.getValue()}
            style={{
              width: "120px",
              border: `1px solid ${statusColor}`,
              borderRadius: "20px",
              color: statusColor,
              padding: "4px 20px",
              height: "26px",
              fontSize: "12px",
              textAlign: "center",
            }}
            onChange={(e) =>
              handleStatusToggle(cell.row.original.id, e.target.value)
            }
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        );
      },
    },

    {
          header: "Actions",
          accessorKey: "actions",
          enableColumnFilter: false,
          cell: (cellProps: any) => {
            const row = cellProps.row.original;
    
            return (
              <ul className="list-inline d-flex justify-content-center align-items-center mb-0">
                {/* Edit */}
                <li 
                className="list-inline-item" 
                title="Edit">
                  <Button
                    color=""
                    className="btn btn-icon btn-soft-primary btn-sm"
                    onClick={() => handleEditClick(row)}>
                    <i className="ri-pencil-fill"></i>
                  </Button>
                </li>
    
                {/* Delete */}
               {/* <li className="list-inline-item" title="Delete">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    // handleDeleteClick(row.id);  
                  }}
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
              </li> */}
              </ul>
            );
          },
        },
    ],
    []
  );

  document.title = "Insurance Products";

  return (
    <div className="page-content">
      <Container
        fluid
        className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4"
      >
        <BreadCrumb title="Insurance" pageTitle={["Insurance", "Products"]} />

        {/* 🔹 SUMMARY CARDS */}
        <Row className="mb-4">
          {[
            ["Total Products", totalProducts],
            ["Active Products", activeProducts],
            ["Inactive Products", inactiveProducts],
          ].map(([title, count], index) => (
            <Col md={3} key={index}>
              <Card className="border-0 shadow-sm rounded-4">
                <CardBody className="p-3">
                  <h6 className="text-muted mb-1">{title}</h6>
                  <h4 className="fw-bold">
                    {String(count).padStart(2, "0")}
                  </h4>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        {/* 🔹 SEARCH + ADD NEW */}
        <Row className="align-items-center g-3 pb-4">
          <Col md={2}>
           <div className="position-relative">
                <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={""}
                placeholder="Filter"
                disabled
                />
                <i
                className="ri-filter-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
                />
            </div>
          </Col>

          <Col md={3}>
          <div className="position-relative">
            <Input
              type="text"
              value={searchQuery}
              placeholder="Search Products..."
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control border ps-5 ms-2 rounded-3"
            />
             <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>

          <Col md="auto" className="ms-auto d-flex justify-content-end">
            <Button
              color="primary"
              className="px-4 rounded-3"
              onClick={toggleModal}
            >
              + Add New
            </Button>
          </Col>
        </Row>

        {/* 🔹 TABLE SECTION */}
        <Card>
          <CardBody style={{ minHeight: "500px" }}>
            {!loading ? (
              filteredData.length > 0 ? (
                <InsuranceContainer
                  columns={columns}
                  data={filteredData}
                  pagination={pagination}
                  setLimit={setLimit}
                  setPage={setPage}
                  limit={limit}
                  setRowId={() => {}}
                  onPageChange={(newPage: number) => {
                  setPage(newPage);
                  dispatch(
                    fetchProductsThunk({
                      page: newPage,
                      per_page: limit,
                      product_code: "",
                      product_category: "",
                      product_name: "",
                      searchkey: searchQuery,
                    })
                  );
                  // SearchPlaceholder="Search Products..."
                }}
                />
              ) : (
                <div className="text-center p-4">No records found.</div>
              )
            ) : (
              <Loader />
            )}
          </CardBody>
        </Card>
      </Container>

      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg" centered>
      <ModalHeader toggle={toggleModal} className="border-0 pb-0">
        <h4 className="fw-bold">
          {isNewProduct ? "New Product" : "Update Product"}
        </h4>
      </ModalHeader>

        <ModalBody>
          <ProductForm
            validation={validation}
            activeTab={activeTab}
            setActiveTab={setActiveTab} 
          toggleModal={toggleModal}
          isNewProduct={isNewProduct}
                  />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default InsuranceRecords;
